/**
 * The opponent's face-down fan — presentation.
 *
 * renderOpponentHand keeps the backs by seat (since 25 September 2026: only
 * the tail grows or shrinks, and a kept back gets no writes) and keeps an
 * unchanged, settled revealed card. It used to rebuild the whole row on every
 * authoritative snapshot, which defeated CSS three times over: a plain
 * `animation` replayed several times a second, the sharpened cardback texture
 * was thrown away with the node, and an arrival was DETACHED mid-flight by the
 * follow-up snapshot the server sends ~60ms behind the one that carried the
 * draw. This module still owns the memory that nodes cannot carry — which
 * backs are new, which already had a texture, what animation is in the air,
 * and which back is leaving — for the nodes that are still rebuilt (a seat
 * whose reveal changes, the next match) and for effects across draws.
 *
 * It is downstream of authority in every direction. It never decides how many
 * cards the opponent holds, never adds or removes an element, never changes a
 * hit target (board.js targets `#opponent-hand .card`, cards.js builds reveals
 * into it, enemy-replay reads it as the draw-flight origin), and never touches
 * a card FACE — every effect ends at `.card-back`, so a revealed card in the
 * same row renders exactly as it does in every other catalog.
 *
 * Contract: tests/unit/ui/enemyFan.test.js.
 */


import { CardBackRaster } from './card-back-raster.js';
let EnemyFan = {};

EnemyFan = (function () {
    // Durations mirror enemy-fan.css exactly — enemyFanMaterial.test.js asserts
    // they stay in step. A JS window shorter than its CSS animation drops the
    // class out from under a running effect, which is the whole family of bug
    // this module exists to close.
    const DECK_TICK_MS = 190;
    const ARRIVE_MS = 420;
    const GLINT_MS = 130;
    const SHOULDER_MS = 300;
    const DEPART_MS = 280;
    const TEXTURE_BLOB_TIMEOUT_MS = 5000;

    // The outer net for a departure nobody ever resolves. What releases the
    // faded-out original is the play itself: the authoritative snapshot that
    // shrinks the hand, or the server settling the action (settleDeparture).
    // This used to be 1.4 s, and a storyteller resolution routinely takes
    // longer: the back the player watched leave snapped back into the fan while
    // the cast was still resolving, then vanished again when the resolution
    // snapshot arrived (25 September 2026). It matches SpellAnnounce's 90 s
    // sticky net for the same resolution.
    const DEPART_HOLD_MS = 90000;

    // Two frames of anticipation on the pile before the card is flicked out of
    // it. Any less and the lift and the departure read as one event.
    const DECK_LEAD_MS = 34;
    const ARRIVE_STAGGER_MS = 76;
    const GLINT_STAGGER_MS = 42;
    const SHOULDER_STAGGER_MS = 46;

    // Where inside the arrival the card actually meets the fan. The flash and
    // the shoulder-aside are CONTACT events; firing them on release would read
    // as the stack reacting to a card that has not arrived yet.
    const CONTACT_MS = Math.round(ARRIVE_MS * 0.56);

    // How far from the change the fan reacts. Beyond this the motion stops
    // being secondary and starts being the whole row moving at once — and in
    // WebKit the frame cost of an animation scales with how many elements
    // carry one, so "local" is both the better read and the cheaper one.
    const SHOULDER_REACH = 2;
    const GLINT_REACH = 4;

    // Slack past an effect before its one-shot class is dropped, so a class can
    // never expire underneath its own animation.
    const CLEAR_PAD_MS = 140;

    // How far, in card widths, an arrival may travel. Measured on the running
    // game, the pile sits 1.2-2 card widths from the fan on the desktop arch
    // (1400x640 through 4K) and 8-11 in the phone lane, where it is on the far
    // side of the play area. Flying the whole way there drags a card straight
    // across the board — which is enemy-replay's gesture for a card being
    // PLAYED, and a secondary event must not borrow the loudest motion on
    // screen. Beyond this the pile contributes its BEARING and nothing else.
    const ORIGIN_REACH_CARDS = 2.6;

    // Card count at the previous render. null = no baseline (first hydration
    // or a new game) → never animate: a re-render is not a draw.
    let lastCount = null;

    // The effect currently in the air: { at, count, arriving, window, steps }.
    // Kept so a rebuild mid-flight can hand it to whatever nodes are in the
    // row now, instead of leaving them undressed.
    let flight = null;

    // The sharpened texture for the fan's current cardback at its current card
    // size, as an object URL. One entry: the fan only ever shows one cardback
    // at one size.
    let texture = null;
    let texturePending = null;

    // The back whose play is still resolving: { count, index, at, node, hand }.
    // A snapshot during the resolution may reseat the backs (a count change, a
    // rebuilt seat), so the fan remembers which one left, and how long ago.
    let departed = null;
    let departTimer = null;
    let currentHand = null;

    function releaseTexture() {
        if (texture?.url) URL.revokeObjectURL(texture.url);
        texture = null;
    }

    function disposePendingTexture() {
        const pending = texturePending;
        texturePending = null;
        if (!pending) return;
        if (pending.blobTimer !== null) clearTimeout(pending.blobTimer);
        pending.observer?.disconnect();
        try { CardBackRaster?.clear?.(pending.probe); } catch (_) { /* noop */ }
        pending.probe.remove();
    }

    function reset() {
        lastCount = null;
        flight = null;
        forgetDeparture();
        // A new match may bring a different opponent cardback; the old texture
        // is dead weight and holds an object URL open.
        disposePendingTexture();
        releaseTexture();
    }

    function backsOf(handEl) {
        return handEl ? [...handEl.querySelectorAll(':scope > .card-back')] : [];
    }

    function forgetDeparture() {
        departed = null;
        if (departTimer !== null) window.clearTimeout(departTimer);
        departTimer = null;
    }

    function releaseDepartedBacks(backs) {
        backs.forEach((back) => {
            back.classList.remove('enemy-fan-departing');
            back.style.removeProperty('--fan-depart-delay');
        });
    }

    /**
     * Keep the departed back out across rebuilds of the same row. A row that
     * KEPT the node is left alone (its timeline is running); a rebuilt row gets
     * the equivalent back faded, resumed where the fade was — a negative delay,
     * so it never plays the departure a second time. The count moving is the
     * server saying the card left: every remaining back is a real card.
     */
    function holdDeparture(backs, count) {
        if (!departed) return;
        if (count !== departed.count) {
            releaseDepartedBacks(backs);
            forgetDeparture();
            return;
        }
        if (backs.includes(departed.node)) return;
        const target = backs[departed.index] || backs[0];
        if (!target) return;
        target.style.setProperty('--fan-depart-delay', `-${Math.max(0, Date.now() - departed.at)}ms`);
        target.classList.add('enemy-fan-departing');
        departed.node = target;
    }

    /** Drop a one-shot class after the animation it drives has finished. */
    function clearLater(el, className, delay) {
        window.setTimeout(() => el.classList.remove(className), delay);
    }

    /** Paint the sharpened texture onto a back, if we already have one. */
    function applyTexture(back) {
        if (!texture || back.dataset.fanTextureKey === texture.key) return;
        back.style.backgroundImage = `url("${texture.url}")`;
        back.dataset.fanTextureKey = texture.key;
    }

    /**
     * Sharpen the fan.
     *
     * A ~736px cardback going into a ~48px phone slot is a 15x reduction that
     * the compositor does in a single step with no mipmaps — the aliasing
     * card-back-raster.js exists to fix, and which the fan (the rotated back
     * that module was written for) was never actually routed through.
     *
     * It is routed through it now, but the texture is handed back as an IMAGE,
     * not as the module's `<canvas>` child. Six or more canvases inside the
     * rotated, overflow-hidden fan cards crash the headless WebKit renderer
     * outright — reproducibly, at three cards fine and at six dead, and with
     * `enemy-fan.css` removed entirely, so it is the canvas elements and not
     * the material. Nothing had hit it before because the fan was the one
     * surface that never used the module.
     *
     * An image costs nothing extra either way: every back is the SAME art at
     * the SAME size, so one reduction serves the whole fan, the browser's own
     * `background-size: cover` does the final 1.5x step, and the texture
     * survives the row being rebuilt without any per-node bookkeeping.
     *
     * Best-effort throughout: the exact CSS background stays visible, so any
     * transient raster failure costs sharpness and nothing else.
     */
    function sharpenFan(handEl, backs, cardbackUrl) {
        const paint = CardBackRaster?.paint;
        if (typeof paint !== 'function' || !backs.length || !cardbackUrl) {
            disposePendingTexture();
            return;
        }

        const lead = backs[0];
        const key = `${cardbackUrl}|${lead.offsetWidth}x${lead.offsetHeight}`;
        if (texture?.key === key) {
            if (texturePending?.key !== key) disposePendingTexture();
            return;      // already applied in present()
        }
        if (texturePending?.key === key) {
            texturePending.handEl = handEl;
            return;
        }
        disposePendingTexture();

        // Raster on a probe, never on a live fan card. Two reasons: the
        // module's canvas must not land inside the rotated stack, and a fan
        // card's own inline background is about to BE the sharpened texture —
        // rastering from it would feed the next pass its own output.
        const probe = document.createElement('div');
        probe.className = 'card card-back';
        probe.setAttribute('aria-hidden', 'true');
        probe.style.cssText = 'position:fixed;left:-9999px;top:0;pointer-events:none;';
        probe.style.width = `${lead.offsetWidth}px`;
        probe.style.height = `${lead.offsetHeight}px`;
        probe.style.backgroundImage = `url("${cardbackUrl}")`;
        document.body.appendChild(probe);

        const pending = {
            key,
            probe,
            handEl,
            observer: null,
            converting: false,
            blobTimer: null,
        };
        texturePending = pending;

        const settle = () => {
            if (texturePending !== pending || pending.converting) return;
            const canvas = probe.querySelector(':scope > canvas[data-card-back-raster]');
            if (!canvas || !(canvas.width > 0) || typeof canvas.toBlob !== 'function') return;
            pending.converting = true;
            pending.blobTimer = setTimeout(() => {
                if (texturePending === pending) disposePendingTexture();
            }, TEXTURE_BLOB_TIMEOUT_MS);
            pending.blobTimer.unref?.();
            try {
                canvas.toBlob((blob) => {
                    if (texturePending !== pending) return;
                    clearTimeout(pending.blobTimer);
                    pending.blobTimer = null;
                    const currentHand = document.getElementById('opponent-hand') || pending.handEl;
                    disposePendingTexture();
                    if (!blob) return;
                    releaseTexture();
                    texture = { key, source: cardbackUrl, url: URL.createObjectURL(blob) };
                    backsOf(currentHand).forEach(applyTexture);
                }, 'image/png');
            } catch (_) {
                disposePendingTexture();
            }
        };

        if (typeof MutationObserver === 'function') {
            pending.observer = new MutationObserver(settle);
            pending.observer.observe(probe, { childList: true });
        }

        Promise.resolve()
            .then(() => paint(probe))
            .then(settle)
            .catch(() => {
                if (texturePending === pending) disposePendingTexture();
            });
    }

    /**
     * The page-space vector from each arriving card's resting place back to the
     * opponent's deck, plus the apex of the arc between them.
     *
     * A drawn card that fades in beside the fan is a `div` appearing. A drawn
     * card that leaves the PILE is a draw. So the arrival is measured against
     * the live deck rather than guessed at: the independent `translate`
     * property resolves in the parent's axis-aligned space, which is exactly
     * the space these page-pixel offsets are in. Guessing would also get the
     * DIRECTION wrong — at 1024x768 the fan is wide enough that the pile ends
     * up on its left, the opposite side from every other tier.
     *
     * Null when the deck has not been built yet (indicators.js creates it on
     * the same snapshot) — the keyframes carry a card-relative fallback so an
     * arrival is never skipped for want of an origin.
     */
    function arrivalOrigins(backs, from, to) {
        const deckRect = document.getElementById('opponent-deck')?.getBoundingClientRect?.();
        if (!deckRect || !(deckRect.width > 0)) return null;
        const deckX = deckRect.left + deckRect.width / 2;
        const deckY = deckRect.top + deckRect.height / 2;

        const origins = new Map();
        for (let index = from; index < to; index += 1) {
            const rect = backs[index]?.getBoundingClientRect?.();
            if (!rect || !(rect.width > 0)) continue;
            const toDeckX = deckX - (rect.left + rect.width / 2);
            const toDeckY = deckY - (rect.top + rect.height / 2);
            const reach = rect.width * ORIGIN_REACH_CARDS;
            const distance = Math.hypot(toDeckX, toDeckY) || 1;
            const pull = distance > reach ? reach / distance : 1;
            const x = toDeckX * pull;
            const y = toDeckY * pull;
            // The fan hangs from the top of the screen on every tier, so the
            // card bows DOWN into the empty board space on its way across
            // instead of dragging through the cards it is about to join.
            const bow = Math.min(Math.abs(x) * 0.2, rect.height * 0.5);
            origins.set(index, {
                '--fan-from-x': `${Math.round(x)}px`,
                '--fan-from-y': `${Math.round(y)}px`,
                '--fan-mid-x': `${Math.round(x * 0.46)}px`,
                '--fan-mid-y': `${Math.round(y * 0.46 + bow)}px`,
            });
        }
        return origins.size ? origins : null;
    }

    /**
     * The steps of the outgoing flight that are still in the air, rebased onto
     * the new one's clock.
     *
     * Two draws a few frames apart arrive as two snapshots, and simply
     * replacing the flight would leave the first card — whose node the second
     * snapshot just rebuilt — with no arrival at all: it would snap into place
     * while the second card flew. Only the card's OWN effects carry; a
     * shoulder-aside is a reaction to an event that has been superseded.
     *
     * Only ever called when the fan GREW. A back carries no identity — the
     * server deliberately nulls which card left — so after a card leaves, the
     * back at index N is not the one that was arriving at index N, and carrying
     * the motion onto it would fly in a card nobody drew.
     */
    function carriedSteps(at, count) {
        if (!flight) return [];
        const elapsed = at - flight.at;
        return flight.steps
            .filter((step) => step.className !== 'enemy-fan-shoulder'
                && step.index < count
                && step.delay + step.duration - elapsed > 0)
            .map((step) => ({ ...step, delay: step.delay - elapsed }));
    }

    /**
     * What the fan is about to do, as data rather than as classes already on
     * elements — so the same decision can be replayed onto the fresh nodes of
     * a snapshot that lands mid-flight.
     */
    function buildFlight(count, previousCount, backs) {
        const arrivedFrom = count > previousCount ? previousCount : count;
        const arriving = arrivedFrom < count;
        const origins = arriving ? arrivalOrigins(backs, arrivedFrom, count) : null;
        const at = Date.now();
        const steps = arriving ? carriedSteps(at, count) : [];
        const stillArriving = new Set(steps
            .filter((step) => step.className === 'enemy-fan-arrive')
            .map((step) => step.index));

        for (let index = arrivedFrom; index < count; index += 1) {
            steps.push({
                index,
                className: 'enemy-fan-arrive',
                delayVar: '--fan-arrive-delay',
                delay: DECK_LEAD_MS + (index - arrivedFrom) * ARRIVE_STAGGER_MS,
                duration: ARRIVE_MS,
                vars: origins?.get(index) || null,
            });
        }

        // The light rakes across the cards whenever the hand changes size —
        // drawn, played, discarded — starting where the change happened and
        // travelling a few cards back into the stack. The far end of a ten-card
        // fan is not involved in what just happened and does not react to it.
        // The card that ARRIVED gets the bright band; the stack it landed on
        // gets the same band, dimmer. One animation, so the two can never
        // contend for the pseudo-element they share.
        const litFrom = Math.max(0, arrivedFrom - GLINT_REACH);
        for (let index = litFrom; index < count; index += 1) {
            const isArrival = index >= arrivedFrom;
            steps.push({
                index,
                className: 'enemy-fan-glint',
                delayVar: '--fan-glint-delay',
                delay: isArrival
                    ? DECK_LEAD_MS + (index - arrivedFrom) * ARRIVE_STAGGER_MS + CONTACT_MS
                    : (arrivedFrom - index) * GLINT_STAGGER_MS,
                duration: GLINT_MS,
                vars: { '--fan-glint-power': isArrival ? '0.9' : '0.36' },
            });
        }

        // Secondary motion: the two cards the new one lands against are
        // shouldered aside and recover. Rotation leads the translation, the way
        // a card nudged at one corner turns before it slides.
        if (arriving) {
            for (let index = Math.max(0, arrivedFrom - SHOULDER_REACH); index < arrivedFrom; index += 1) {
                // A card still flying in from the previous draw cannot also be
                // a bystander shouldered aside by this one: both drive the
                // card's own translate/rotate, and two animations on one
                // property do not compose — the later simply wins.
                if (stillArriving.has(index)) continue;
                const distance = arrivedFrom - index;
                steps.push({
                    index,
                    className: 'enemy-fan-shoulder',
                    delayVar: '--fan-shoulder-delay',
                    delay: CONTACT_MS + distance * SHOULDER_STAGGER_MS,
                    duration: SHOULDER_MS,
                    vars: { '--fan-shoulder-power': distance === 1 ? '1' : '0.5' },
                });
            }
        }

        return {
            at,
            count,
            arriving,
            window: steps.reduce((longest, step) => Math.max(longest, step.delay + step.duration), 0),
            steps,
        };
    }

    /**
     * Dress the row with a flight, `elapsed` ms after it began.
     *
     * A negative `animation-delay` is CSS's own resume: it starts the animation
     * as if it had already been running for that long. So a card whose node was
     * detached 60ms into its arrival picks the arrival back up at 60ms on the
     * node that replaced it, instead of snapping back to the start.
     */
    function applyFlight(backs, elapsed) {
        for (const step of flight.steps) {
            const back = backs[step.index];
            // A node that still carries the class is the SAME node, still
            // animating. Rewriting its delay would shift a running timeline and
            // make the card jump.
            if (!back || back.classList.contains(step.className)) continue;
            const remaining = step.delay + step.duration - elapsed;
            if (remaining <= 0) continue;
            for (const [name, value] of Object.entries(step.vars || {})) {
                back.style.setProperty(name, value);
            }
            back.style.setProperty(step.delayVar, `${step.delay - elapsed}ms`);
            back.classList.add(step.className);
            clearLater(back, step.className, remaining + CLEAR_PAD_MS);
        }
    }

    /**
     * Anticipation on the pile itself, so the card has somewhere to come FROM.
     * The deck is indicators.js's element and is only ever moved, never
     * rebuilt, so it needs no part in the flight.
     */
    function tickDeck() {
        const deck = document.getElementById('opponent-deck');
        if (!deck || deck.classList.contains('enemy-fan-deck-tick')) return;
        deck.classList.add('enemy-fan-deck-tick');
        clearLater(deck, 'enemy-fan-deck-tick', DECK_TICK_MS + CLEAR_PAD_MS);
    }

    /**
     * Dress the rebuilt fan.
     *
     * @param {HTMLElement} handEl   the #opponent-hand row, already rebuilt
     * @param {object}      options
     * @param {string}      options.cardbackUrl the opponent's resolved cardback
     * @param {boolean}     options.animate     false during a mulligan reshuffle,
     *                                          which changes the count without
     *                                          anyone drawing
     */
    function present(handEl, { cardbackUrl = '', animate = true } = {}) {
        // Never let a missing row become a baseline of zero — the next real
        // render would then look like the opponent drawing their whole hand.
        if (!handEl) return;
        const backs = backsOf(handEl);
        const count = backs.length;
        const previousCount = lastCount;
        lastCount = count;
        currentHand = handEl;
        holdDeparture(backs, count);

        // A raster belongs to one semantic source. Rebuilt nodes must never
        // receive the previous back while a changed/custom-pending source is
        // resolving; release it before applyTexture gets a chance to dress
        // even one card with stale pixels.
        if (!cardbackUrl || (texture && texture.source !== cardbackUrl)) {
            releaseTexture();
        }

        // One read phase per render. The writes below do not depend on layout,
        // the reads inside sharpenFan and arrivalOrigins do, and the flight's
        // own writes are custom properties and class names — neither of which
        // invalidates the layout those reads just paid for.
        backs.forEach((back, index) => {
            back.style.setProperty('--fan-index', String(index));
            applyTexture(back);
        });
        sharpenFan(handEl, backs, cardbackUrl);

        if (!animate) { flight = null; return; }

        if (previousCount !== null && count !== previousCount) {
            flight = buildFlight(count, previousCount, backs);
            if (flight.arriving) tickDeck();
            applyFlight(backs, 0);
            return;
        }

        // Nothing changed — but the row may have just been rebuilt UNDER an
        // effect that is still in the air. The server does not send one
        // snapshot per event: a draw is routinely followed ~60ms later by
        // another snapshot carrying the same hand size, and that one detaches
        // every animating node. Without this the arrival died at ~66ms of 420.
        if (!flight || flight.count !== count) { flight = null; return; }
        const elapsed = Date.now() - flight.at;
        if (elapsed >= flight.window) { flight = null; return; }
        applyFlight(backs, elapsed);
    }

    /**
     * The back whose clone is currently flying to the board. enemy-replay
     * animates a COPY, so without this the card is briefly on screen twice —
     * once in the fan, once mid-flight. Fades the one left behind; the
     * authoritative snapshot is still what actually removes it, and the class
     * expires on its own if the play never resolves.
     */
    function markDeparting(el) {
        if (!el?.classList?.contains?.('card-back')) return;
        const backs = backsOf(el.parentElement);
        forgetDeparture();
        departed = { count: backs.length, index: backs.indexOf(el), at: Date.now(), node: el };
        el.classList.add('enemy-fan-departing');
        departTimer = window.setTimeout(settleDeparture, DEPART_HOLD_MS);
    }

    /**
     * The server settled the opponent's play: whatever the fan still holds out
     * comes back now. A failed cast, or a spell whose caster draws exactly one
     * card, leaves the count where it was — this is the truthful moment.
     */
    function settleDeparture() {
        const hands = new Set([currentHand, departed?.node?.parentElement].filter(Boolean));
        forgetDeparture();
        hands.forEach((hand) => releaseDepartedBacks(backsOf(hand)));
    }

    /**
     * Whether a sharpened texture is still on its way to the fan. The match
     * loading screen presents a new board only once the fan wears it
     * (ui/match-loading.js); a fan that could not be sharpened keeps its exact
     * CSS back and reports false.
     */
    function isSharpening() {
        return texturePending !== null;
    }

    return { reset, present, markDeparting, settleDeparture, isSharpening };
})();

export { EnemyFan };
